// Auth context — exposes session state, login (verify_otp), logout.
//
// On mount, calls /api/auth/session to determine if the user is authenticated.
// Login/logout invalidate the session-query and the react-query cache.

import { createContext, useContext, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from './lib/orpc.js';
import { is_marketing_host } from './lib/marketing.js';

export interface Session {
  tenant_id: string;
  staff_id: string;
  scopes: string[];
  kind: 'staff' | 'subscriber' | 'platform_admin';
}

interface AuthState {
  loading: boolean;
  session: Session | null;
  send_otp: (phone: string) => Promise<{ ttl_seconds: number }>;
  verify_otp: (phone: string, otp: string) => Promise<Session>;
  login_password: (email: string, password: string) => Promise<Session>;
  set_password: (token: string, password: string) => Promise<Session>;
  request_password_reset: (email: string) => Promise<void>;
  /** Subscriber-portal login (separate from staff send_otp/verify_otp). */
  send_otp_subscriber: (phone: string) => Promise<{ ttl_seconds: number }>;
  verify_otp_subscriber: (phone: string, otp: string) => Promise<Session>;
  logout: () => Promise<void>;
}

const ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [loading, set_loading] = useState(true);
  const [session, set_session] = useState<Session | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    // The public marketing apex (chitdesk.com / www) is a static page with no
    // tenant context — skip the session probe entirely. It avoids a needless
    // cross-subdomain API call (and its CORS round-trip) on the landing.
    if (is_marketing_host()) {
      set_loading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await orpc.auth.session({});
        if (cancelled) return;
        if (r.authenticated) {
          set_session({
            tenant_id: r.tenant_id,
            staff_id: r.staff_id,
            scopes: r.scopes,
            kind: r.kind,
          });
        }
      } finally {
        if (!cancelled) set_loading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value: AuthState = {
    loading,
    session,
    send_otp: async (phone) => orpc.auth.send_otp({ phone }),
    verify_otp: async (phone, otp) => {
      const r = await orpc.auth.verify_otp({ phone, otp });
      const s: Session = {
        tenant_id: r.tenant_id,
        staff_id: r.staff_id,
        scopes: r.scopes,
        kind: 'staff',
      };
      set_session(s);
      await qc.invalidateQueries();
      return s;
    },
    login_password: async (email, password) => {
      const r = await orpc.auth.login_password({ email, password });
      const s: Session = {
        tenant_id: r.tenant_id,
        staff_id: r.staff_id,
        scopes: r.scopes,
        kind: 'staff',
      };
      set_session(s);
      await qc.invalidateQueries();
      return s;
    },
    set_password: async (token, password) => {
      const r = await orpc.auth.set_password({ token, password });
      const s: Session = {
        tenant_id: r.tenant_id,
        staff_id: r.staff_id,
        scopes: r.scopes,
        kind: 'staff',
      };
      set_session(s);
      await qc.invalidateQueries();
      return s;
    },
    request_password_reset: async (email) => {
      await orpc.auth.request_password_reset({ email });
    },
    send_otp_subscriber: async (phone) => orpc.auth.send_otp_subscriber({ phone }),
    verify_otp_subscriber: async (phone, otp) => {
      const r = await orpc.auth.verify_otp_subscriber({ phone, otp });
      const s: Session = {
        tenant_id: r.tenant_id,
        staff_id: r.subscriber_id, // session.sub field — for subscribers, holds subscriber_id
        scopes: ['subscriber:self'],
        kind: 'subscriber',
      };
      set_session(s);
      await qc.invalidateQueries();
      return s;
    },
    logout: async () => {
      await orpc.auth.logout({});
      set_session(null);
      qc.clear();
    },
  };

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(ctx);
  if (!v) throw new Error('useAuth must be inside <AuthProvider>');
  return v;
}
